export { PAYOUTS_SCOPE } from "./navigation.ts";

import type { BeneficiaryPayoutSummary } from "@tdelivr/api-client/generated/finance/payout.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the payouts board does to the payouts FIN-07 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `payouts`:
 *
 * ```
 * payouts:['Payouts and statements','FIN-06 · FIN-07','What you have earned, what the platform kept, and when you get paid.',
 *   [['Due Friday','KES 612,300.00','waiting','PYO-0928M'],['Commission this week','KES 73,476.00','done','12%'],
 *    ['Refunds deducted','KES 3,120.00','caution','substitutions'],['Paid this month','KES 2.1M','done','']],
 *   ['Payout','Period','Net'],
 *   [['PYO-0928M','22–28 Sep','KES 612,300.00','human','awaiting approval'],
 *    ['PYO-0921M','15–21 Sep','KES 598,410.00','done','paid'],
 *    ['PYO-0914M','8–14 Sep','KES 571,020.00','done','paid']],
 *   'info','Each statement lists orders, commission, refunds and the net amount.']
 * ```
 *
 * ## The recorded blocker was right about both halves, which is the first time in this cohort
 *
 * It read: _"Nothing publishes the period a payout is for — not payout.periodStart, not
 * payout.periodEnd, not batch.periodStart — and the design's middle column is that period. FIN-07's
 * batch listing is also the tenant's rather than this merchant's, so a merchant reading it would see
 * every other merchant's payouts."_
 *
 * **Both sentences are true.** Three screens in this plan had notes that were wrong about which half
 * was missing — FIN-09's named a published field, MER-02's named a published field and missed the
 * route, the replies board's was wrong on three of four counts — so this one was re-measured
 * expecting the same, and it held. The second half is the one [ADR 0089](../../../docs/adr/0089-a-payout-has-a-beneficiary-and-only-the-tenant-could-read-one.md)
 * answers, by publishing a beneficiary-scoped listing. **The period is still absent and this board
 * draws it as one.**
 *
 * ## Three columns: one published, one a uuid where the design draws a code, one with no source
 *
 * | column   | source                                                                         |
 * | -------- | ------------------------------------------------------------------------------ |
 * | `Payout` | `payoutId`, a uuid. The design writes `PYO-0928M`; nothing mints a payout code |
 * | `Period` | **nothing.** No start, no end, no statement a payout settles                   |
 * | `Net`    | `amountMinor` with `currency`, in full minor units                             |
 *
 * `Payout` is drawn rather than marked absent, because a uuid *is* the identifier a merchant would
 * quote in a question about their money — it is the same decision the finance console's own boards
 * take, and the opposite of the reports board's refusal to turn `revenue.daily` into
 * `Daily revenue by brand`. Showing an identifier in the shape the service publishes it is not
 * inventing anything; deriving `PYO-0928M` from it would be.
 *
 * ## `Period` is the absence, and `requestedAt` is not a substitute for it
 *
 * The design's middle cell is a **range a payout covers** — `22–28 Sep` — and FIN-07 holds
 * `requestedAt`, the instant the payout was asked for. Those are different facts, and the near miss
 * is what makes the temptation worth naming: a payout requested on 28 September plausibly covers
 * the week ending then, and "plausibly" is the whole of the argument. A week derived by subtracting
 * seven days would be this board inventing a settlement calendar, and the one it invented would be
 * wrong for any tenant that pays fortnightly, monthly or on demand.
 *
 * So the cell is the absence, and `requestedAt` is drawn in the row detail in its own right, named
 * as what it is. **The honest fix is one field on a payout**, or a statement record a payout
 * settles, and either is a change to FIN-07 rather than to this screen.
 *
 * ## None of the four tiles is drawn, and they are four different absences
 *
 * `Due Friday · KES 612,300.00` needs a payout **date** — nothing holds one; a payout has a state
 * and the instants it changed, and no service says when money will arrive. `Commission this week ·
 * 12%` is FIN-06's and is a sum over a period this board does not have. `Refunds deducted ·
 * substitutions` is FIN-02's, and nothing links a refund to a payout. `Paid this month · KES 2.1M`
 * is a sum over a bounded listing, which is R-04.
 *
 * **And `KES 2.1M` would be refused even if the sum were published**, because an amount on this
 * platform is shown with its currency code and in full minor units. `2.1M` is neither.
 *
 * ## The design's `Update bank details` form is refused on a ground a scope would not fix
 *
 * `Method`, `Till or account number`, `Account name`, and "Changes need confirmation by SMS to the
 * owner." FIN-07 takes a `beneficiaryReference` on `POST /v1/payouts` — per payout, from the
 * service that creates it — and publishes no command to change a beneficiary's bank destination at
 * all. There is no route to call with any scope, and ADR 0089 withholds the current value from this
 * board deliberately, so the form cannot even show what it would be changing.
 */

/**
 * The design's two badge words, and the six states that reach neither.
 *
 * The design draws `human · awaiting approval` and `done · paid`. FIN-07 publishes eight states, so
 * six of them have no designed row and take a class the design does use with the service's own
 * word — the decision ADR 0088 took for NTF-07's `draft`, and for the same reason: a state shown
 * under a word the design happens to have is worse than one shown under its own.
 *
 * `outcomeUncertain` wins over all of them, and that precedence is the one judgement in this
 * function. A payout whose submission to the rail did not answer may or may not have been paid, and
 * a merchant shown `failed` concludes they were not and acts on it with their own supplier. The
 * design's `unknown` class — dashed, grey, borrowing neither success nor failure — is what that
 * draws, which is `07 Payment Outcome Unknown.dc.html`'s whole subject applied to one cell.
 */
export function stateOf(payout: BeneficiaryPayoutSummary): {
  classKey: StateClassKey;
  label: string;
  title: string;
} {
  if (payout.outcomeUncertain)
    return {
      classKey: "unknown",
      label: "outcome unknown",
      title:
        "FIN-07 submitted this payout to the bank and the bank did not answer, so the money may or may not have moved. It is not shown as failed, because that would tell you that you were not paid.",
    };
  switch (payout.state) {
    case "CREATED":
      return {
        classKey: "human",
        label: "awaiting approval",
        title:
          "FIN-07 publishes CREATED. Two members of the tenant's finance staff approve a payout.",
      };
    case "PAID":
      return { classKey: "done", label: "paid", title: "FIN-07 publishes PAID." };
    case "APPROVED":
      return {
        classKey: "waiting",
        label: "approved",
        title:
          "FIN-07 publishes APPROVED. The design draws no row for this state, so the word is the service's.",
      };
    case "SUBMITTED":
      return {
        classKey: "waiting",
        label: "submitted",
        title:
          "FIN-07 publishes SUBMITTED — sent to the bank. The design draws no row for this state.",
      };
    case "PROCESSING":
      return {
        classKey: "waiting",
        label: "processing",
        title:
          "FIN-07 publishes PROCESSING — the bank has it. The design draws no row for this state.",
      };
    case "FAILED":
      return {
        classKey: "caution",
        label: "failed",
        title:
          "FIN-07 publishes FAILED and no reason. The design draws no row for this state, and nothing on this platform publishes why a transfer failed.",
      };
    case "RETURNED":
      return {
        classKey: "caution",
        label: "returned",
        title:
          "FIN-07 publishes RETURNED — the bank sent it back. The design draws no row for this state.",
      };
    case "CANCELLED":
      return {
        classKey: "closed",
        label: "cancelled",
        title: "FIN-07 publishes CANCELLED. The design draws no row for this state.",
      };
  }
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(payouts: readonly BeneficiaryPayoutSummary[]): readonly string[] {
  return [...new Set(payouts.map((payout) => stateOf(payout).label))];
}

/**
 * The design's `Net` cell: the currency code and the full minor units, and never a rounded figure.
 *
 * `KES 612,300.00`, which is the design's own format and `CLAUDE.md` section 2's rule. The tile the
 * design writes as `KES 2.1M` is not drawn at all, and this is the function that is the reason:
 * there is no code path here that can produce an abbreviated amount, so none can reach a cell.
 *
 * Two decimal places because every currency on this platform is minor-unit-of-100. A zero-decimal
 * currency drawn this way would be a hundredfold error, so the one assumption is stated rather than
 * buried: the day FIN-07 accepts JPY or UGX-without-cents, this function is wrong and
 * `tests/payouts.test.ts` names the assumption so the failure lands here.
 */
export function netCell(payout: BeneficiaryPayoutSummary): string {
  const major = Math.trunc(payout.amountMinor / 100);
  const minor = Math.abs(payout.amountMinor % 100);
  const grouped = major.toLocaleString("en-US");
  return `${payout.currency} ${grouped}.${String(minor).padStart(2, "0")}`;
}

/** Why the design's middle column has no cell, said in the cell. */
export const PERIOD_ABSENT =
  "Nothing on this platform holds the period a payout covers — not a start, not an end, not a statement it settles. FIN-07 publishes requestedAt, which is when the payout was asked for, and a week derived by subtracting seven days from it would be this portal inventing a settlement calendar. One field on a payout would fix it.";

/**
 * One row of the payouts board, and the detail panel under it.
 *
 * The design's grid has three columns and this record publishes more fields than that, so the rest
 * goes in the panel — the established shape for every board in this portal. What the panel does
 * **not** contain is the half ADR 0089 withholds: no bank reference, no ledger account, no provider
 * reference, no approving subjects. A panel is not a place those become acceptable.
 */
export function payoutRow(payout: BeneficiaryPayoutSummary): BoardRow {
  const state = stateOf(payout);
  return {
    id: payout.payoutId,
    cells: [
      {
        /*
         * The design writes `PYO-0928M`; FIN-07 publishes a uuid, and nothing on this platform mints
         * a payout code. Drawn rather than marked absent, because a uuid **is** the identifier a
         * merchant would quote in a question about their money — showing it in the shape the service
         * publishes it invents nothing, where deriving `PYO-0928M` from it would.
         */
        text: payout.payoutId,
        mono: true,
        title:
          "FIN-07's payoutId. The design draws a short code like PYO-0928M; nothing on this platform mints one, and this is the identifier to quote in a question about this payment.",
      },
      {
        /*
         * The design's `22–28 Sep`, and the one column on this board with no source at all.
         *
         * `requestedAt` is the near miss and is the reason this comment exists: a payout requested
         * on 28 September plausibly covers the week ending then, and "plausibly" is the whole of the
         * argument. A week derived by subtracting seven days would be this board inventing a
         * settlement calendar, wrong for any tenant that pays fortnightly, monthly or on demand.
         */
        text: "",
        absent: true,
        title: PERIOD_ABSENT,
      },
      {
        text: netCell(payout),
        mono: true,
        title: `FIN-07 publishes ${String(payout.amountMinor)} minor units of ${payout.currency}, which is what this cell shows in full.`,
      },
    ],
    state,
    /** The design tints its `human` and `failed` rows, and never as the only signal. */
    ...(state.classKey === "human" || state.classKey === "caution" ? { tinted: true } : {}),
    detail: [
      { label: "Net", value: netCell(payout), mono: true },
      { label: "Period", value: "—", absent: true },
      { label: "Requested", value: asDay(payout.requestedAt) },
      ...(payout.batchId === undefined
        ? [{ label: "Batch", value: "—", absent: true }]
        : [{ label: "Batch", value: payout.batchId, mono: true }]),
      { label: "Bank destination", value: "—", absent: true },
    ],
    history: [
      { label: "Last changed", value: asDay(payout.updatedAt) },
      { label: "State", value: payout.state, mono: true },
    ],
    glyph: "payments",
  };
}

/**
 * The panel's own absences, in words, because `DetailField` carries no title.
 *
 * Three of the five fields above are drawn and two are `—`, and a reader looking at a dash needs
 * the reason rather than a hunt. `Period` is the design's middle column over again; the bank
 * destination is withheld on purpose, which is a different kind of nothing and says so.
 */
export const DETAIL_NOTES: readonly { readonly label: string; readonly note: string }[] = [
  { label: "Period", note: PERIOD_ABSENT },
  {
    label: "Batch",
    note: "A payout can belong to no batch — batchId is optional on FIN-07's create command. Where there is one, it is the single handle you and the platform's finance team both hold for this payment.",
  },
  {
    label: "Bank destination",
    note: "Withheld deliberately. ADR 0089 keeps the account FIN-07 credits off this board, which is also why the design's Update bank details form cannot show what it would change.",
  },
  {
    label: "Requested",
    note: "FIN-07's requestedAt — when this payout was asked for. Not the period it covers, which nothing holds.",
  },
];

/** What the board says about itself, under the grid. */
export const LISTING_NOTE =
  "These are the payouts you are the beneficiary of. The period each one covers is not shown because nothing on this platform records it — FIN-07 holds when a payout was asked for, not what it settles. One field on a payout would fix it.";

/** The design's own closing note, kept word for word. */
export const BOARD_NOTE = "Each statement lists orders, commission, refunds and the net amount.";

/** Why the design's bank-details form cannot be completed, on a ground a scope would not fix. */
export const BANK_DETAILS_REFUSAL =
  "FIN-07 publishes no command to change a beneficiary's bank destination. The reference it credits is supplied per payout by the service that creates one, so there is no route this form could call with any scope — and ADR 0089 withholds the current value from this board, so it could not show what it was changing.";

/** Why this portal cannot say which merchant it is acting for, when the claim is missing. */
export const NO_MERCHANT_CLAIM =
  "Your sign-in does not say which shop you work for, so this board cannot ask for your payouts. It needs a merchant_id claim on the token.";

/*
 * There is no filtering, ordering or paging in this module, and that is deliberate rather than
 * missing. `board.ts`'s `boardPage` and `ORDER_CHOICES` are what every grid in this portal draws
 * through, and a board that wrote its own would be a second opinion about the design's toolbar. The
 * one thing this file contributes to it is `statesPresent`, because only this file knows which of
 * FIN-07's eight states are badge words.
 *
 * The count under the grid comes from `boundedNote`, for the same reason: the sentence a reader
 * sees when a listing is cut short is one sentence on this platform, not one per board.
 */
